export type HttpMethod = "GET" | "POST" | "PATCH" | "PUT" | "DELETE";

export type ApiClient = {
  request<T>(
    path: string,
    method?: HttpMethod,
    body?: unknown,
    extraHeaders?: HeadersInit,
    signal?: AbortSignal
  ): Promise<T>;
  get<T>(path: string, headers?: HeadersInit): Promise<T>;
  post<T>(path: string, body?: unknown, headers?: HeadersInit): Promise<T>;
  patch<T>(path: string, body?: unknown, headers?: HeadersInit): Promise<T>;
  put<T>(path: string, body?: unknown, headers?: HeadersInit): Promise<T>;
  del<T>(path: string, body?: unknown, headers?: HeadersInit): Promise<T>;
  /**
   * POSTs `body` and returns the response body as a byte stream, for routes
   * that answer with a streamed response (e.g. NDJSON — read it with
   * `readNdjson`). A non-2xx response throws the same `ApiError` as the other
   * methods, so a caller only ever receives a stream on success.
   */
  stream(
    path: string,
    body?: unknown,
    options?: ApiStreamOptions
  ): Promise<ReadableStream<Uint8Array>>;
};

export type ApiStreamOptions = {
  signal?: AbortSignal;
  headers?: HeadersInit;
};

export type ApiClientOptions = {
  /**
   * If omitted, requests use relative URLs (best for same-origin apps).
   * For external apps use "https://punchup.live" or env-configured value.
   */
  baseUrl?: string;
  /**
   * Optional bearer token getter (external apps).
   * Not used unless baseUrl is provided (see attachAuthForAbsoluteBaseUrl).
   */
  getBearerToken?: () => Promise<string | null>;
  /**
   * Optional: retry once on 401 by invalidating token cache upstream.
   * (Implement invalidation in your token provider; see below.)
   */
  onUnauthorized?: () => Promise<void> | void;
};

export function createApiClient(opts: ApiClientOptions = {}): ApiClient {
  const { baseUrl, getBearerToken, onUnauthorized } = opts;

  const resolveUrl = (path: string) => (baseUrl ? `${baseUrl}${path}` : path); // path should be "/api/..."

  /** Sends the request (auth, JSON encoding, one 401 retry) and returns the raw Response. */
  async function send(
    path: string,
    method: HttpMethod,
    body: unknown,
    extraHeaders: HeadersInit | undefined,
    signal: AbortSignal | undefined,
    accept: string,
    didRetry = false
  ): Promise<Response> {
    const url = resolveUrl(path);
    const headers = new Headers(extraHeaders);

    const isFormData =
      typeof FormData !== "undefined" && body instanceof FormData;
    const hasJsonBody =
      body !== undefined &&
      body !== null &&
      typeof body === "object" &&
      !isFormData;

    if (hasJsonBody) {
      headers.set("content-type", "application/json;charset=UTF-8");
      headers.set("accept", accept);
    }

    if (baseUrl && getBearerToken) {
      const token = await getBearerToken();
      if (token) headers.set("authorization", `Bearer ${token}`);
    }

    let res: Response;
    try {
      res = await fetch(url, {
        method,
        headers,
        body: hasJsonBody ? JSON.stringify(body) : (body as BodyInit),
        signal,
      });
    } catch (cause) {
      throw new ApiError({
        kind: "network",
        message: `${method} ${path}: Network error`,
        method,
        url,
        cause,
      });
    }

    // 401 retry once (optional)
    if (res.status === 401 && baseUrl && !didRetry && onUnauthorized) {
      await onUnauthorized();
      return send(path, method, body, extraHeaders, signal, accept, true);
    }

    return res;
  }

  async function httpError(
    res: Response,
    method: HttpMethod,
    path: string
  ): Promise<ApiError> {
    const { raw, json } = await readBody(res);
    // Prefer message fields if present
    const fields =
      json && typeof json === "object"
        ? (json as Record<string, unknown>)
        : null;
    const messageFromJson = fields
      ? fields.message || fields.error || fields.detail
      : null;

    return new ApiError({
      kind: "http",
      message:
        typeof messageFromJson === "string"
          ? `${method} ${path}: (${res.status}) ${messageFromJson}`
          : `${method} ${path}: (${res.status}) ${res.statusText || "Request failed"}`,
      method,
      url: resolveUrl(path),
      status: res.status,
      statusText: res.statusText,
      headers: headersToObject(res.headers),
      data: json ?? undefined,
      raw: raw || undefined,
    });
  }

  async function doFetch<T>(
    path: string,
    method: HttpMethod,
    body?: unknown,
    extraHeaders?: HeadersInit,
    signal?: AbortSignal
  ): Promise<T> {
    const res = await send(
      path,
      method,
      body,
      extraHeaders,
      signal,
      "application/json"
    );

    if (!res.ok) throw await httpError(res, method, path);

    const { raw, json } = await readBody(res);

    // 204 No Content (or any empty success) — nothing to parse
    if (res.status === 204 || !raw) return undefined as T;

    if (json !== null) return json as T;

    // If you *require* JSON:
    throw new ApiError({
      kind: "parse",
      message: `${method} ${path}: (${res.status}) Expected JSON but got non-JSON response`,
      method,
      url: resolveUrl(path),
      status: res.status,
      statusText: res.statusText,
      headers: headersToObject(res.headers),
      raw: raw || undefined,
    });
  }

  async function doStream(
    path: string,
    body?: unknown,
    options: ApiStreamOptions = {}
  ): Promise<ReadableStream<Uint8Array>> {
    const res = await send(
      path,
      "POST",
      body,
      options.headers,
      options.signal,
      "application/x-ndjson"
    );

    if (!res.ok) throw await httpError(res, "POST", path);

    if (!res.body) {
      throw new ApiError({
        kind: "parse",
        message: `POST ${path}: (${res.status}) Expected a streamed response body`,
        method: "POST",
        url: resolveUrl(path),
        status: res.status,
        statusText: res.statusText,
        headers: headersToObject(res.headers),
      });
    }

    return res.body;
  }

  return {
    request: (path, method = "GET", body, headers, signal) =>
      doFetch(path, method, body, headers, signal),
    get: (path, headers) => doFetch(path, "GET", undefined, headers),
    post: (path, body, headers) => doFetch(path, "POST", body, headers),
    patch: (path, body, headers) => doFetch(path, "PATCH", body, headers),
    put: (path, body, headers) => doFetch(path, "PUT", body, headers),
    del: (path, body, headers) => doFetch(path, "DELETE", body, headers),
    stream: (path, body, options) => doStream(path, body, options),
  };
}

async function readBody(
  res: Response
): Promise<{ raw: string; json: unknown | null }> {
  const raw = await res.text();
  let json: unknown | null = null;
  try {
    json = raw ? JSON.parse(raw) : null;
  } catch {
    json = null;
  }
  return { raw, json };
}

function headersToObject(headers: Headers): Record<string, string> {
  const out: Record<string, string> = {};
  headers.forEach((v, k) => (out[k] = v));
  return out;
}

function safeJsonParse(text: string): any | null {
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

// For tests
export function createMockApiClient(): ApiClient {
  return {
    request: jest.fn(),
    get: jest.fn(),
    post: jest.fn(),
    patch: jest.fn(),
    put: jest.fn(),
    del: jest.fn(),
    stream: jest.fn(),
  };
}

export type ApiErrorKind =
  | "http" // server responded non-2xx
  | "network" // fetch threw (DNS, offline, CORS, etc)
  | "parse" // expected JSON but couldn't parse
  | "timeout"; // optional, if you add timeouts

export class ApiError extends Error {
  kind: ApiErrorKind;
  method: string;
  url: string;

  status?: number;
  statusText?: string;
  headers?: Record<string, string>;

  // Body info (one or both may be set)
  data?: unknown; // parsed JSON, if any
  raw?: string; // raw response text, if any

  constructor(init: {
    message: string;
    kind: ApiErrorKind;
    method: string;
    url: string;
    status?: number;
    statusText?: string;
    headers?: Record<string, string>;
    data?: unknown;
    raw?: string;
    cause?: unknown;
  }) {
    super(init.message);
    this.name = "ApiError";
    this.kind = init.kind;
    this.method = init.method;
    this.url = init.url;
    this.status = init.status;
    this.statusText = init.statusText;
    this.headers = init.headers;
    this.data = init.data;
    this.raw = init.raw;

    // Keep causal chain if supported
    // @ts-ignore TS lib target differences
    this.cause = init.cause;
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}
